import React, { useMemo, useRef, useEffect, useState } from 'react';
import { sanitizeEmailBodyForDisplay } from '../utils/sanitizeEmailBody';
import { THUNDERBIRD_VIEWER_BLOCK_CSS } from '../utils/thunderbirdParagraphStyles';

interface EmailBodyViewProps {
  bodyHtml: string;
  emailId: number;
  apiBaseUrl: string;
  /** When set, inline image URLs include ?partitionAt= for composite email lookup */
  emailPartitionAt?: string | null;
  className?: string;
}

/**
 * Renders email HTML in an iframe so it matches classic email clients.
 * Keeps all styles (inline + style/link tags) and only sanitizes for XSS.
 * Scripts are stripped so the sandboxed iframe does not trigger "Blocked script execution" warnings.
 */
const EmailBodyView: React.FC<EmailBodyViewProps> = ({
  bodyHtml,
  emailId,
  apiBaseUrl,
  emailPartitionAt,
  className = '',
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(120);

  const sanitizedHtml = useMemo(
    () => sanitizeEmailBodyForDisplay(bodyHtml, emailId, apiBaseUrl, emailPartitionAt),
    [bodyHtml, emailId, apiBaseUrl, emailPartitionAt]
  );

  const iframeSrcdoc = useMemo(() => {
    if (!sanitizedHtml) return '';
    const bodyStyle =
      'margin:0;padding:8px;background:#ffffff;color:#111827;font-family:system-ui,-apple-system,Segoe UI,sans-serif;font-size:14px;line-height:normal';
    const linkStyle =
      `<style>html,body{overflow:hidden}body{min-height:0}${THUNDERBIRD_VIEWER_BLOCK_CSS}body div>span:only-child:empty::before{content:"\\00a0"}img{max-width:100%;height:auto}img[width][height]{max-width:100%;height:auto}</style>`;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank" rel="noopener noreferrer">${linkStyle}</head><body style="${bodyStyle}">${sanitizedHtml}</body></html>`;
  }, [sanitizedHtml]);

  useEffect(() => {
    if (!iframeRef.current || !sanitizedHtml) return;
    const iframe = iframeRef.current;
    const timeoutIds: number[] = [];

    const measure = () => {
      try {
        const doc = iframe.contentDocument;
        if (doc?.body) {
          const height = Math.max(120, doc.documentElement.scrollHeight);
          setIframeHeight(height);
        }
      } catch {
        // Sandbox may block access
      }
    };

    const onLoad = () => {
      measure();
      timeoutIds.push(window.setTimeout(measure, 400));
      timeoutIds.push(window.setTimeout(measure, 1200));
    };
    iframe.addEventListener('load', onLoad);
    return () => {
      iframe.removeEventListener('load', onLoad);
      timeoutIds.forEach(clearTimeout);
    };
  }, [sanitizedHtml]);

  if (!bodyHtml) return null;

  return (
    <iframe
      ref={iframeRef}
      title="Email body"
      srcDoc={iframeSrcdoc}
      sandbox="allow-same-origin"
      scrolling="no"
      className={className}
      style={{
        width: '100%',
        height: iframeHeight,
        minHeight: 120,
        border: 'none',
        display: 'block',
        overflow: 'hidden',
      }}
    />
  );
};

export default EmailBodyView;
